import React, { useState } from 'react';
import { Modal, Form, Input, Button, Alert, Typography, message } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { authService } from '../services/api';
import { PASSWORD_MIN } from '../utils/password';

const { Text } = Typography;

/**
 * Cambio de clave obligatorio cuando la vigente es TEMPORAL.
 *
 * Se monta a nivel de app, no de una pantalla: la cabaña le blanquea la clave
 * al comprador y se la pasa por teléfono o WhatsApp, así que esa clave viajó
 * por un canal que no controlamos. Si el cambio no fuera obligatorio, quedaría
 * viva indefinidamente y el blanqueo terminaría dejando el sistema PEOR que
 * antes: una clave conocida por dos personas y sin vencimiento.
 *
 * Bloqueante a propósito — sin cruz, sin cerrar por click afuera, sin Escape.
 * La única salida es cambiarla o desloguearse; de lo contrario alcanzaría con
 * cerrar el modal para seguir usando la cuenta con la clave temporal.
 *
 * Pide la clave temporal de nuevo aunque el usuario la acaba de tipear en el
 * login: reusa `/auth/cambiar-password` tal como está, sin abrir un segundo
 * camino para cambiar claves. Un endpoint que cambia la clave SIN pedir la
 * actual es superficie nueva, y no vale la pena por ahorrar un campo.
 */
const CambioClaveObligatorio = () => {
  const { user, updateUser, logout } = useAuth();
  const [form] = Form.useForm();
  const [guardando, setGuardando] = useState(false);

  if (!user?.debe_cambiar_password) return null;

  const handleSubmit = async (values) => {
    setGuardando(true);
    try {
      await authService.cambiarPassword({
        password_actual: values.password_actual,
        password_nuevo: values.password_nuevo,
      });
      // El backend ya apagó el flag; se refleja acá para que el modal se cierre
      // sin necesidad de volver a pedir el usuario.
      updateUser({ debe_cambiar_password: false });
      form.resetFields();
      message.success('Listo, tu clave quedó cambiada');
    } catch (err) {
      message.error(err.response?.data?.error || 'No pudimos cambiar la clave');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal
      open
      title={<><KeyOutlined /> Elegí tu nueva clave</>}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={460}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Estás usando una clave temporal"
        description="Te la generó la cabaña para que puedas entrar. Elegí una nueva para seguir."
      />

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="password_actual"
          label="Clave temporal"
          rules={[{ required: true, message: 'Ingresá la clave temporal que te pasaron' }]}
        >
          <Input.Password placeholder="La que usaste para entrar" autoFocus />
        </Form.Item>

        <Form.Item
          name="password_nuevo"
          label="Nueva clave"
          rules={[
            { required: true, message: 'Elegí una clave nueva' },
            { min: PASSWORD_MIN, message: `Mínimo ${PASSWORD_MIN} caracteres` },
          ]}
        >
          <Input.Password placeholder={`Mínimo ${PASSWORD_MIN} caracteres`} />
        </Form.Item>

        <Form.Item
          name="password_repetir"
          label="Repetir la nueva clave"
          dependencies={['password_nuevo']}
          rules={[
            { required: true, message: 'Repetí la clave nueva' },
            // Se valida acá y no contra un state: `getFieldValue` lee el valor
            // del momento en que antd corre la regla, así que no hay forma de
            // que quede desincronizado con lo que el usuario ve.
            ({ getFieldValue }) => ({
              validator: (_, value) =>
                !value || getFieldValue('password_nuevo') === value
                  ? Promise.resolve()
                  : Promise.reject(new Error('Las dos claves no coinciden')),
            }),
          ]}
        >
          <Input.Password placeholder="Escribila de nuevo" />
        </Form.Item>

        <Button type="primary" htmlType="submit" loading={guardando} block>
          Cambiar clave
        </Button>
      </Form>

      {/* Escape hatch: sin esto, alguien que no puede completar el cambio
          (se equivocó de cuenta, perdió la clave temporal) queda encerrado en
          un modal que no cierra. */}
      <div style={{ textAlign: 'center', marginTop: 12 }}>
        <Button type="link" size="small" onClick={logout}>
          <Text type="secondary">Cerrar sesión</Text>
        </Button>
      </div>
    </Modal>
  );
};

export default CambioClaveObligatorio;
